import { ArrowDownOutlined, ArrowUpOutlined, PlusOutlined, ReloadOutlined, SwapOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Descriptions, Row, Space, Table, Tag, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';

import type { WalletBalance, WalletEntry } from '../../../src/types.ts';
import { api, errorText, type CustomerProfile, type Transaction } from '../lib/api.ts';
import { fmtDateTime, fmtMoney, fmtPrice } from '../lib/format.ts';

interface Props {
  wallet: WalletBalance | null;
  walletError: string | null;
  onRefreshWallet: () => void;
  onAddFunds: () => void;
  onTransfer: () => void;
  /** Bumps whenever a top-up or transfer may have changed the history. */
  version: number;
}

const STATUS_COLOR: Record<string, string> = {
  PAYED: 'green',
  PENDING_PAYMENT: 'processing',
  NOT_PAYED: 'default',
  CANCELLED: 'default',
  EXPIRED: 'default',
  ERROR: 'red',
  REVERSED: 'orange',
  FULLY_REFUNDED: 'orange',
  PARTLY_REFUNDED: 'orange',
};

const nice = (s: string | undefined | null): string => (s ? s.replace(/_/g, ' ').toLowerCase() : '');

/** Balance, the two money actions, and the movement history. */
export function WalletPage({ wallet, walletError, onRefreshWallet, onAddFunds, onTransfer, version }: Props) {
  const [history, setHistory] = useState<WalletEntry[] | null>(null);
  const [historyTotal, setHistoryTotal] = useState<number | null>(null);
  const [tx, setTx] = useState<Transaction[] | null>(null);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.walletHistory().then((r) => {
        setHistory(r.items);
        setHistoryTotal(r.total);
      }),
      api.transactions().then((r) => setTx(r.items)).catch(() => setTx([])),
      api.profile().then(setProfile).catch(() => {}),
    ])
      .catch((e) => setError(errorText(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load, version]);

  return (
    <div className="page">
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={10}>
          <Card className="wallet-card" variant="borderless">
            <Typography.Text className="wallet-card__label">Wallet balance</Typography.Text>
            <div className="wallet-card__amount">
              {wallet ? (
                <>
                  {fmtMoney(wallet.balance)} <small>{wallet.currency}</small>
                </>
              ) : walletError ? (
                '—'
              ) : (
                '…'
              )}
            </div>
            {walletError && <Alert type="warning" showIcon message={walletError} style={{ marginBottom: 12 }} />}
            <Space wrap>
              <Button type="primary" icon={<PlusOutlined />} onClick={onAddFunds} className="wallet-card__btn">
                Add funds
              </Button>
              <Button icon={<SwapOutlined />} onClick={onTransfer} className="wallet-card__btn wallet-card__btn--ghost">
                Transfer
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => { onRefreshWallet(); load(); }} className="wallet-card__btn wallet-card__btn--ghost" loading={loading}>
                Refresh
              </Button>
            </Space>
          </Card>
          {profile && (
            <Card size="small" title="Receive money" style={{ marginTop: 16 }}>
              <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
                Another etravel user can send to this wallet with your mobile number.
              </Typography.Paragraph>
              <Descriptions
                size="small"
                column={1}
                items={[
                  ...(profile.name ? [{ key: 'n', label: 'Name', children: profile.name }] : []),
                  ...(profile.mobileNo || profile.phone
                    ? [{ key: 'm', label: 'Mobile', children: <Typography.Text copyable>{String(profile.mobileNo || profile.phone)}</Typography.Text> }]
                    : []),
                  ...(profile.email ? [{ key: 'e', label: 'Email', children: <Typography.Text copyable>{profile.email}</Typography.Text> }] : []),
                  ...(profile.currency ? [{ key: 'c', label: 'Currency', children: profile.currency }] : []),
                ]}
              />
            </Card>
          )}
        </Col>
        <Col xs={24} lg={14}>
          <Card
            size="small"
            title={
              <Space>
                <span>Wallet movements</span>
                {historyTotal != null && <Tag bordered={false}>{historyTotal}</Tag>}
              </Space>
            }
          >
            {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
            <Table<WalletEntry>
              size="small"
              rowKey="id"
              loading={history === null && !error}
              dataSource={history ?? []}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              locale={{ emptyText: 'No movements yet.' }}
              columns={[
                { title: 'When', dataIndex: 'createdDate', width: 170, render: (v: string) => fmtDateTime(v) },
                {
                  title: 'Type',
                  dataIndex: 'walletAction',
                  width: 110,
                  render: (v: string) =>
                    v === 'DEPOSIT' ? (
                      <Tag color="green" bordered={false} icon={<ArrowDownOutlined />}>
                        in
                      </Tag>
                    ) : (
                      <Tag color="red" bordered={false} icon={<ArrowUpOutlined />}>
                        out
                      </Tag>
                    ),
                },
                { title: 'Notes', dataIndex: 'notes', ellipsis: true, render: (v: string | null) => v || <Typography.Text type="secondary">—</Typography.Text> },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  align: 'right',
                  width: 140,
                  render: (v: number, r) => (
                    <strong style={{ color: r.walletAction === 'DEPOSIT' ? '#2e7d32' : '#c62828' }}>
                      {r.walletAction === 'DEPOSIT' ? '+' : '−'}
                      {fmtPrice(Math.abs(v), r.currency)}
                    </strong>
                  ),
                },
              ]}
            />
          </Card>
          <Card size="small" title="Top-ups & payments" style={{ marginTop: 16 }}>
            <Table<Transaction>
              size="small"
              rowKey="id"
              loading={tx === null}
              dataSource={tx ?? []}
              pagination={{ pageSize: 8, hideOnSinglePage: true }}
              locale={{ emptyText: 'No transactions yet.' }}
              columns={[
                { title: 'When', dataIndex: 'createdDate', width: 170, render: (v: string) => fmtDateTime(v) },
                { title: 'Type', dataIndex: 'transactionType', render: (v: string) => nice(v) },
                { title: 'Via', dataIndex: 'paymentType', render: (v: string) => nice(v) },
                {
                  title: 'Status',
                  dataIndex: 'transactionStatus',
                  render: (v: string) => (
                    <Tag bordered={false} color={STATUS_COLOR[v] ?? 'default'}>
                      {nice(v)}
                    </Tag>
                  ),
                },
                { title: 'Amount', align: 'right', width: 140, render: (_: unknown, r) => fmtPrice(r.total ?? r.amount ?? null, r.currency) },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
